import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, UsersRound, X } from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getReferralCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import ReferralIncentiveTable from "./ReferralIncentiveTable";
import { formatEarningsCurrency } from "../earningsAdapters";
import "./ReferralIncentivesPage.css";

const ReferralIncentive = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { referralCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getReferralCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  return (
    <section className="referral-page">
      <div className="referral-shell">
        <header className="referral-header">
          <div className="referral-heading">
            <span className="referral-heading-icon">
              <UsersRound size={24} strokeWidth={2} />
            </span>
            <div>
              <span className="referral-kicker">REFERRAL INCENTIVES</span>
              <h1>Referral Incentives</h1>
              <p>
                Track referral earnings, partner network activity and
                incentive status.
              </p>
            </div>
          </div>

          <div className="referral-summary">
            <span>Total Received</span>
            <strong>
              {formatEarningsCurrency(
                referralCommission.totalReferralCommission,
              )}
            </strong>
          </div>
        </header>

        <div className="referral-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="referral-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="referral-search">
            <Search size={18} strokeWidth={2} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search referral incentives..."
              aria-label="Search referral incentives"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <ReferralIncentiveTable data={referralCommission.data || []} />

        <Pagenation
          currentPage={page}
          totalPages={referralCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default ReferralIncentive;
